/**
 * Associate view (route /teams/associate). The core grouping flow: show the
 * venue's still-unassociated per-event team rows as a multi-select list, then
 * either assign the selected rows to an existing canonical team or spin up a
 * new team from them. This is how weekly name-changers get folded into one
 * persistent identity. Also supports merging two canonical teams.
 *
 * With ?seasonId=<id> the list shows only rows from the game nights in that
 * season. The season standings screen links here with that filter.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  Heading,
  Select,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes';
import VenuePicker from './VenuePicker';
import api from '../../utils/api';
import {
  assignToTeam,
  createTeam,
  fetchTeams,
  fetchUnassociated,
  fetchVenueOptions,
  fmtDate,
  mergeTeam,
  type TeamSummary,
  type UnassociatedRow,
  type VenueOption,
} from './types';
import './Teams.css';

const AssociateView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [venueId, setVenueId] = useState<number | null>(null);
  // Optional season filter from ?seasonId=. Cleared when the host picks
  // another venue or asks for all nights.
  const [seasonId, setSeasonId] = useState<number | null>(
    () => Number(searchParams.get('seasonId')) || null,
  );
  const [seasonName, setSeasonName] = useState<string | null>(null);
  const [rows, setRows] = useState<UnassociatedRow[]>([]);
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Assign-to-existing target
  const [assignTargetId, setAssignTargetId] = useState<string>('');

  // New-team-from-selection dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');

  // Merge dialog
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState<string>('');
  const [mergeTargetId, setMergeTargetId] = useState<string>('');

  // Resolve venues, seed venueId from ?venueId= or first venue.
  useEffect(() => {
    let active = true;
    fetchVenueOptions()
      .then((vs) => {
        if (!active) return;
        setVenues(vs);
        const fromUrl = Number(searchParams.get('venueId'));
        if (fromUrl && vs.some((v) => v.id === fromUrl)) setVenueId(fromUrl);
        else if (vs[0]) setVenueId(vs[0].id);
        else setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Failed to load venues.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (vid: number, sid: number | null = null) => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const [unassoc, list] = await Promise.all([
        fetchUnassociated(vid, sid),
        fetchTeams(vid),
      ]);
      setRows(unassoc);
      setTeams(list.teams ?? []);
    } catch {
      setError('Failed to load unassociated rows.');
      setRows([]);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (venueId !== null) {
      load(venueId, seasonId);
      const params: Record<string, string> = { venueId: String(venueId) };
      if (seasonId) params.seasonId = String(seasonId);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, seasonId, load]);

  // Show the season name in the filter notice.
  useEffect(() => {
    if (!seasonId) {
      setSeasonName(null);
      return;
    }
    let active = true;
    api
      .get(`/api/seasons/${seasonId}`)
      .then((res) => {
        if (active) setSeasonName(res.data?.data?.season?.name ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [seasonId]);

  const pickVenue = (id: number) => {
    setSeasonId(null);
    setVenueId(id);
  };

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.liveTeamId)));
  };

  const handleAssignExisting = async () => {
    if (!assignTargetId || selectedIds.length === 0 || venueId === null) return;
    setBusy(true);
    setError(null);
    try {
      await assignToTeam(Number(assignTargetId), selectedIds);
      setAssignTargetId('');
      await load(venueId, seasonId);
    } catch {
      setError('Failed to assign rows to the selected team.');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateFromSelection = async () => {
    const name = newName.trim();
    if (!name || selectedIds.length === 0 || venueId === null) return;
    setBusy(true);
    setError(null);
    try {
      const team = await createTeam(name, venueId);
      await assignToTeam(team.id, selectedIds);
      setNewName('');
      setNewOpen(false);
      await load(venueId, seasonId);
    } catch {
      setError('Failed to create a team from the selected rows.');
    } finally {
      setBusy(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeSourceId || !mergeTargetId || mergeSourceId === mergeTargetId || venueId === null)
      return;
    setBusy(true);
    setError(null);
    try {
      // Fold the source team into the target.
      await mergeTeam(Number(mergeSourceId), Number(mergeTargetId));
      setMergeOpen(false);
      setMergeSourceId('');
      setMergeTargetId('');
      await load(venueId, seasonId);
    } catch {
      setError('Failed to merge teams.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="teams-page">
      <div className="teams-header">
        <div>
          <Heading size="6">Associate teams</Heading>
          <Text size="2" color="gray">
            Group per-event team rows — including weekly name-changers — into one persistent
            identity.
          </Text>
        </div>
        <Flex gap="2">
          <Button variant="soft" color="gray" onClick={() => navigate('/teams')}>
            Back to teams
          </Button>
          <Button variant="soft" onClick={() => setMergeOpen(true)} disabled={teams.length < 2}>
            Merge teams
          </Button>
        </Flex>
      </div>

      <div className="teams-toolbar">
        <Text size="2" color="gray">
          Venue:
        </Text>
        <VenuePicker venues={venues} value={venueId} onChange={pickVenue} />
      </div>

      {seasonId && (
        <Callout.Root mb="3" color="blue" role="status">
          <Callout.Text>
            Showing only teams from the season {seasonName ? `“${seasonName}”` : `#${seasonId}`}.{' '}
            <Text
              as="span"
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate(`/seasons/${seasonId}`)}
            >
              Back to the standings
            </Text>{' '}
            or{' '}
            <Text
              as="span"
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setSeasonId(null)}
            >
              show all nights
            </Text>
            .
          </Callout.Text>
        </Callout.Root>
      )}

      {error && <div className="teams-error">{error}</div>}

      {/* Bulk action bar — only meaningful with a selection. */}
      {selectedIds.length > 0 && (
        <Flex
          align="center"
          gap="3"
          mb="3"
          p="3"
          style={{
            background: 'var(--gray-a2)',
            border: '1px solid var(--gray-a4)',
            borderRadius: 'var(--radius-3)',
            flexWrap: 'wrap',
          }}
        >
          <Badge color="crimson" variant="soft">
            {selectedIds.length} selected
          </Badge>
          <Flex align="center" gap="2">
            <Text size="2" color="gray">
              Assign to:
            </Text>
            <Select.Root value={assignTargetId} onValueChange={setAssignTargetId}>
              <Select.Trigger placeholder="existing team…" />
              <Select.Content>
                {teams.map((t) => (
                  <Select.Item key={t.id} value={String(t.id)}>
                    {t.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <Button onClick={handleAssignExisting} disabled={busy || !assignTargetId}>
              Assign
            </Button>
          </Flex>
          <Button variant="soft" onClick={() => setNewOpen(true)} disabled={busy}>
            New team from selection
          </Button>
        </Flex>
      )}

      {loading ? (
        <div className="teams-empty">Loading unassociated rows…</div>
      ) : rows.length === 0 ? (
        <div className="teams-empty">
          <Text as="p" size="3" weight="medium">
            Nothing to associate.
          </Text>
          <Text as="p" size="2" color="gray">
            {seasonId
              ? 'Every team in this season is linked to a persistent identity.'
              : 'Every per-event team for this venue is already linked to a persistent identity.'}
          </Text>
        </div>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label="Select all"
                />
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Name used</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Quiz</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">Date</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Players</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((r) => {
              const checked = selected.has(r.liveTeamId);
              return (
                <Table.Row
                  key={r.liveTeamId}
                  className="teams-assoc-row"
                  onClick={() => toggle(r.liveTeamId)}
                  style={checked ? { background: 'var(--crimson-a3)' } : undefined}
                >
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(r.liveTeamId)}
                      aria-label={`Select ${r.name}`}
                    />
                  </Table.Cell>
                  <Table.RowHeaderCell>{r.name}</Table.RowHeaderCell>
                  <Table.Cell>
                    <Text color="gray">{r.quizName}</Text>
                  </Table.Cell>
                  <Table.Cell align="right">{fmtDate(r.date)}</Table.Cell>
                  <Table.Cell align="center">{r.playerCount ?? '—'}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      )}

      {/* New team from selection */}
      <Dialog.Root open={newOpen} onOpenChange={setNewOpen}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>New team from selection</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            Create a persistent team and attach the {selectedIds.length} selected row
            {selectedIds.length === 1 ? '' : 's'} to it.
          </Dialog.Description>
          <TextField.Root
            placeholder="Team name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFromSelection();
            }}
            autoFocus
          />
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreateFromSelection} disabled={busy || !newName.trim()}>
              {busy ? 'Creating…' : 'Create & assign'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Merge two canonical teams */}
      <Dialog.Root open={mergeOpen} onOpenChange={setMergeOpen}>
        <Dialog.Content maxWidth="460px">
          <Dialog.Title>Merge teams</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            Fold one team’s history into another. The source team is removed and all its rows
            move to the target.
          </Dialog.Description>
          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="1" mb="1" color="gray">
                Source (will be removed)
              </Text>
              <Select.Root value={mergeSourceId} onValueChange={setMergeSourceId}>
                <Select.Trigger placeholder="Select source team…" style={{ width: '100%' }} />
                <Select.Content>
                  {teams.map((t) => (
                    <Select.Item key={t.id} value={String(t.id)}>
                      {t.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </label>
            <label>
              <Text as="div" size="1" mb="1" color="gray">
                Target (kept)
              </Text>
              <Select.Root value={mergeTargetId} onValueChange={setMergeTargetId}>
                <Select.Trigger placeholder="Select target team…" style={{ width: '100%' }} />
                <Select.Content>
                  {teams
                    .filter((t) => String(t.id) !== mergeSourceId)
                    .map((t) => (
                      <Select.Item key={t.id} value={String(t.id)}>
                        {t.name}
                      </Select.Item>
                    ))}
                </Select.Content>
              </Select.Root>
            </label>
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              color="amber"
              onClick={handleMerge}
              disabled={busy || !mergeSourceId || !mergeTargetId || mergeSourceId === mergeTargetId}
            >
              {busy ? 'Merging…' : 'Merge'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
};

export default AssociateView;
